import { createStorefrontApiClient } from '@shopify/storefront-api-client';
import { shopifyURL } from './config';

const storefrontClient = createStorefrontApiClient({
  storeDomain: shopifyURL,
  apiVersion: '2024-07',
  publicAccessToken: document.body.dataset.shopifyStorefrontApiKey,
});

function transformProductNode(node) {
  return {
    id: node.id,
    availableForSale: node.availableForSale,
    createdAt: node.createdAt,
    updatedAt: node.updatedAt,
    descriptionHtml: node.descriptionHtml,
    description: node.description,
    handle: node.handle,
    productType: node.productType,
    title: node.title,
    vendor: node.vendor,
    publishedAt: node.publishedAt,
    onlineStoreUrl: node.onlineStoreUrl,
    tags: node.tags,
    options: node.options,
    images: node.images?.edges.map(edge => ({
      ...edge.node,
      src: edge.node.url,
    })),
    variants: node.variants?.edges.map(edge => ({
      ...edge.node,
      image: edge.node.image,
      price: edge.node.price,
      compareAtPrice: edge.node.compareAtPrice,
      selectedOptions: edge.node.selectedOptions,
      sku: edge.node.sku,
    })),
  };
}

const ShopifyService = {
  buildQuery() {
    return `
      query getProducts($first: Int, $query: String, $sortKey: ProductSortKeys, $reverse: Boolean, $after: String, $before: String, $last: Int) {
        products(first: $first, query: $query, sortKey: $sortKey, reverse: $reverse, after: $after, before: $before, last: $last) {
          pageInfo {
            hasNextPage
            hasPreviousPage
            startCursor
            endCursor
          }
          edges {
            cursor
            node {
              id
              availableForSale
              createdAt
              updatedAt
              descriptionHtml
              description
              handle
              productType
              title
              vendor
              publishedAt
              onlineStoreUrl
              tags
              options {
                name
                values
              }
              images(first: 250) {
                edges {
                  node {
                    id
                    url
                    altText
                  }
                }
              }
              variants(first: 250) {
                edges {
                  node {
                    id
                    title
                    price {
                      amount
                      currencyCode
                    }
                    weight
                    availableForSale
                    sku
                    compareAtPrice {
                      amount
                      currencyCode
                    }
                    image {
                      id
                      url
                      altText
                    }
                    selectedOptions {
                      name
                      value
                    }
                  }
                }
              }
            }
          }
        }
      }
    `;
  },

  async fetchQuery(baseArgs, ids = [], systemProducts = false) {
    try {
      if ((ids.length === 0) && systemProducts) {
        return { edges: [], pageInfo: {} };
      }

      const query = this.buildQuery();

      const idsQuery = ids.map(id => `id:"${id}"`).join(' OR ');
      const args = {
        ...baseArgs,
        query: baseArgs.query ? `${baseArgs.query} AND (${idsQuery})` : idsQuery
      };
      const variables = { ...args };

      if (variables.before) {
        delete variables.first;
      } else if (variables.after) {
        delete variables.last;
      }

      const { data, errors } = await storefrontClient.request(query, {
        variables,
      });

      if (errors) {
        console.error('GraphQL errors in fetchQuery:', errors);
        return { edges: [], pageInfo: {} };
      }

      let edges = data.products.edges.map(edge => transformProductNode(edge.node));

      edges = edges.filter(product => !product.tags?.includes('Refill'));

      return {
        edges,
        pageInfo: data.products.pageInfo,
      };
    } catch (error) {
      console.error('Error in fetchQuery:', error);
      return { edges: [], pageInfo: {} };
    }
  },


  async fetch(id) {
    const gid = String(id).startsWith('gid://') ? String(id) : `gid://shopify/Product/${id}`;

    const query = `
      query getProduct($id: ID!) {
        product(id: $id) {
          id
          availableForSale
          createdAt
          updatedAt
          descriptionHtml
          description
          handle
          productType
          title
          vendor
          publishedAt
          onlineStoreUrl
          tags
          options {
            name
            values
          }
          images(first: 250) {
            edges {
              node {
                id
                url
                altText
              }
            }
          }
          variants(first: 250) {
            edges {
              node {
                id
                title
                price {
                  amount
                  currencyCode
                }
                compareAtPrice {
                  amount
                  currencyCode
                }
                image {
                  url
                }
                selectedOptions {
                  name
                  value
                }
              }
            }
          }
        }
      }
    `;

    try {
      const { data, errors } = await storefrontClient.request(query, {
        variables: { id: gid },
      });

      if (errors) {
        console.error('GraphQL errors in fetch:', errors);
        return null;
      }

      return transformProductNode(data.product);
    } catch (error) {
      console.error('Error in fetch:', error);
      return null;
    }
  },
};

export default ShopifyService;
